import type { ComputedRef, Ref } from 'vue'

import {
    type CatalogueSortOrder,
    type GemsApiStoneAnalyticsFragment,
    type NonSkuGemstoneAnalyticsFragment,
    type NonSkuLabGrownAnalyticsFragment,
    type ProductAnalyticsFragment,
    type ProductListAnalyticsFragment,
    type RegionCode,
    type RingBuilderCentreStoneFragmentFragment,
    type SalePrice,
} from '@/graphql/api'

import { assertNever } from '@/helpers'
import { useAbsoluteUrl } from '@/composables/app/useAbsoluteUrl'
import { useRoutes } from '@/router/routes'
import { useRegionPlugin } from '@/plugins/region'

import { type AppCurrency, useCurrency } from '@/plugins/currency.ts'
import { useAuth } from '@/plugins/auth.ts'
import type { RingBuilder } from '@/plugins/ring-builder.ts'

export function getProductCategory<
    TProduct extends
        | Pick<ProductAnalyticsFragment, '__typename'>
        | Pick<ProductListAnalyticsFragment, '__typename'>,
>(product: TProduct): string {
    switch (product.__typename) {
        case 'CatalogueEngagementRing':
            return 'Engagement Ring'
        case 'CatalogueReadyToGoRing':
            return 'Express Engagement Ring'
        case 'CatalogueWeddingRing':
            return 'Wedding Ring'
        case 'CatalogueJewelleryBracelet':
            return 'Bracelet'
        case 'CatalogueJewelleryNecklace':
            return 'Necklace'
        case 'CatalogueJewelleryEarrings':
            return 'Earrings'
        case 'CatalogueJewelleryRing':
            return 'Ring'
        default:
            return assertNever(product.__typename, '')
    }
}

function getBandWidth(product: ProductAnalyticsFragment): number | null {
    switch (product.__typename) {
        case 'CatalogueEngagementRing':
        case 'CatalogueWeddingRing':
            return product.bandWidth
        case 'CatalogueReadyToGoRing':
        case 'CatalogueJewelleryBracelet':
        case 'CatalogueJewelleryNecklace':
        case 'CatalogueJewelleryEarrings':
        case 'CatalogueJewelleryRing':
            return null
        default:
            return assertNever(product, null)
    }
}

function getDesignComplexity(product: ProductAnalyticsFragment): string {
    switch (product.__typename) {
        case 'CatalogueEngagementRing':
        case 'CatalogueReadyToGoRing':
        case 'CatalogueJewelleryNecklace':
        case 'CatalogueJewelleryBracelet':
        case 'CatalogueJewelleryEarrings':
        case 'CatalogueJewelleryRing':
        case 'CatalogueWeddingRing':
            return 'Base'
        default:
            return assertNever(product, 'Base')
    }
}

function getJewellerySku(
    product: ProductAnalyticsFragment | ProductListAnalyticsFragment
): string | null {
    switch (product.__typename) {
        case 'CatalogueJewelleryNecklace':
        case 'CatalogueJewelleryBracelet':
        case 'CatalogueJewelleryEarrings':
        case 'CatalogueJewelleryRing':
            return product.jewellerySku ?? ''
        case 'CatalogueWeddingRing':
        case 'CatalogueEngagementRing':
        case 'CatalogueReadyToGoRing':
            return null
        default:
            return assertNever(product, null)
    }
}

function parsePrice(price: Pick<SalePrice, 'total'>): number {
    return parseFloat(price.total.amount || '0')
}

type SegmentCenterGem = {
    gem_price: number | null // price in main currency units, the price of the gem in the currency which is currently selected.
    crystal: string | null // the name of the crystal
    shape: string | null // the name of the shape
    color: string | null // the name of the color
    clarity: string | null // the name of the clarity
    cut: string | null // the name of the cut
    grade: string | null // the name of the grade, or null if diamond
    carat_weight: number // the carat weight of the stone, not formatted anyhow,
    supplier_vendor: string | null // Gems API vendor name, null for the time being
    country_of_origin: string | null // Country of origin, or null
    gcode: number | null // ATM always null
    nonSku: 'Yes' | 'No' // Yes if Non-Sku, No otherwise
}

function getGemsApiStone(
    stone: GemsApiStoneAnalyticsFragment
): Omit<SegmentCenterGem, 'gem_price'> {
    return {
        crystal: stone.crystalWithInfo.info?.translation.name ?? null,
        shape: stone.shapeWithInfo.info?.translation.name ?? null,
        color: stone.colorWithInfo.info?.translation.name ?? null,
        clarity: stone.clarityWithInfo.info?.translation.name ?? null,
        cut: stone.cutWithInfo?.info?.translation.name ?? null,
        grade:
            stone.crystalWithInfo.info?.systemCode !== 'DIAMOND'
                ? (stone.gradeWithInfo?.info?.translation.name ?? null)
                : null,
        carat_weight: stone.weight,
        supplier_vendor: null,
        country_of_origin: stone.originCountry,
        gcode: null,
        nonSku: 'No',
    }
}

function getNonSkuGemstone(
    stone: NonSkuGemstoneAnalyticsFragment
): Omit<SegmentCenterGem, 'gem_price'> {
    return {
        crystal: stone.crystal.translation.name,
        shape: stone.shape.translation.name,
        color: stone.color.translation.name,
        clarity: stone.clarity.translation.name,
        cut: stone.cut.translation.name,
        grade: stone.colorGrade.translation.name,
        carat_weight: parseFloat(stone.caratWeight),
        supplier_vendor: null,
        country_of_origin: null,
        gcode: null,
        nonSku: 'Yes',
    }
}

function getNonSkuLabGrown(
    stone: NonSkuLabGrownAnalyticsFragment
): Omit<SegmentCenterGem, 'gem_price'> {
    return {
        crystal: stone.crystal.translation.name,
        shape: stone.shape.translation.name,
        color: stone.color.translation.name,
        clarity: stone.clarity.translation.name,
        cut: stone.cut.translation.name,
        grade: null,
        carat_weight: parseFloat(stone.caratWeight),
        supplier_vendor: null,
        country_of_origin: null,
        gcode: null,
        nonSku: 'Yes',
    }
}

function getEngagementRingCenterStone(
    rbCentreStone: RingBuilderCentreStoneFragmentFragment
): SegmentCenterGem | null {
    if (!rbCentreStone.stone) return null

    const gem_price = parseFloat(rbCentreStone.totalSalePrice.amount)

    switch (rbCentreStone.stone.__typename) {
        case 'GemsApiStone':
            if (!rbCentreStone.stone.stone) {
                return null
            }

            return {
                gem_price,
                ...getGemsApiStone(rbCentreStone.stone.stone),
            }

        case 'NonSkuGemstone':
            return {
                gem_price,
                ...getNonSkuGemstone(rbCentreStone.stone),
            }
        case 'NonSkuLabGrown':
            return {
                gem_price,
                ...getNonSkuLabGrown(rbCentreStone.stone),
            }
        default:
            return assertNever(rbCentreStone.stone, null)
    }
}

type SegmentProductAnalyticsSerialized = {
    user_id: string | null
    currency: AppCurrency
    value: number
    products: SegmentProductAnalyticsProduct[]
}

type SegmentProductAnalyticsProduct = {
    category: string
    price: number
    quantity: 1
    name: string
    url?: string
    suffix: string | null
    sku: any
    description: string | null
    design_complexity: string
    jewellery_sku: string | null
    metal_type1: string
    metal_weight1: null
    metal_type2: null
    metal_weight2: null
    signature_gem: string | undefined
    band_width: number | null
    delivery_days: number
    gcode: null
    total_exc_tax_amount: null
    center_gem?: SegmentCenterGem | null
}

type SegmentProductAnalyticsNormalizer = (
    product: ProductAnalyticsFragment,
    ringBuilder?: RingBuilder
) => SegmentProductAnalyticsSerialized

export function useSegmentProductAnalytics(): SegmentProductAnalyticsNormalizer {
    const { user } = useAuth()

    return (
        product: ProductAnalyticsFragment,
        ringBuilder?: RingBuilder
    ): SegmentProductAnalyticsSerialized => {
        const centreStoneFragment = ringBuilder?.centreStoneFragment
        const mountFragment = ringBuilder?.mountFragment
        const price = Number(
            ringBuilder?.totalSaleAmount.value || parsePrice(product.salePrice)
        )

        return {
            user_id: user.value?.id || null,
            currency: product.salePrice.total.currency,
            value: price,
            products: [
                {
                    category: getProductCategory(product),
                    price,
                    quantity: 1,
                    name: product.segmentName,
                    suffix: product.suffix,
                    sku: product.catalogueCode,
                    description: product.translation.description,
                    design_complexity: getDesignComplexity(product),
                    jewellery_sku: getJewellerySku(product),
                    metal_type1: product.primaryMetal.segmentName,
                    metal_weight1: null,
                    metal_type2: null,
                    metal_weight2: null,
                    signature_gem:
                        mountFragment?.value?.signatureStone?.segmentName,
                    band_width: getBandWidth(product),
                    delivery_days: product.deliveryDays,
                    gcode: null,
                    total_exc_tax_amount: null,
                    center_gem: centreStoneFragment?.value
                        ? getEngagementRingCenterStone(
                              centreStoneFragment.value
                          )
                        : undefined,
                },
            ],
        }
    }
}

export type SegmentAnalyticsProductList = {
    user_id: string | null
    list_id: string
    currency: AppCurrency
    region: RegionCode
    list_category: string
    sort_by: CatalogueSortOrder
    page_number: number
    products: SegmentAnalyticsProductForList[]
}

export type SegmentAnalyticsProductForList = {
    category: string
    price: number
    quantity: 1
    name: string
    url: string
    sku: string
    metal_type_1: string
    description: string | null
    jewellery_sku: string | null
    position: number
}

type SegmentProductAnalyticsNormalizerForList = (
    products: ProductListAnalyticsFragment[],
    index?: number
) => SegmentAnalyticsProductList

export function useSegmentProductListAnalytics(
    listId: ComputedRef<string>,
    category: ComputedRef<string>,
    sortBy: Readonly<Ref<CatalogueSortOrder>>,
    page: Readonly<Ref<number>>
): SegmentProductAnalyticsNormalizerForList {
    const absoluteUrl = useAbsoluteUrl()
    const { regionCode } = useRegionPlugin()
    const currency = useCurrency()

    const routes = useRoutes()
    const { user } = useAuth()

    const getProductAnalytics = (
        product: ProductListAnalyticsFragment,
        index: number
    ): SegmentAnalyticsProductForList => ({
        category: getProductCategory(product),
        price: parsePrice(product.salePrice),
        quantity: 1,
        name: product.segmentName,
        url: absoluteUrl(routes.ring.product(product)).href,
        sku: product.catalogueCode,
        metal_type_1: product.primaryMetal.segmentName,
        description: product.translation.description,
        jewellery_sku: getJewellerySku(product),
        position: index + 1,
    })

    return (
        products: ProductListAnalyticsFragment[],
        index?: number
    ): SegmentAnalyticsProductList => {
        const getPA =
            index === undefined
                ? getProductAnalytics
                : (product: ProductListAnalyticsFragment) =>
                      getProductAnalytics(product, index)

        return {
            user_id: user.value?.id || null,
            list_id: listId.value,
            currency: currency.value,
            region: regionCode.value,
            list_category: category.value,
            sort_by: sortBy.value,
            page_number: page.value,
            products: products.map(getPA),
        }
    }
}
